<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import { useAuthStore } from '@/store/auth'
import { messageOf } from '@/utils/messages'
import { documentFrame } from '@/service/learning-frame.mjs'
// 복습 학습(레거시 학습종료 과정의 "수강하기"). 학습기간이 끝난 수강의 차시를 복습기간 동안 다시 본다.
// 학습 세션·진도·수료에는 반영하지 않는다. 파일 원본 증명은 10분이라 화면을 여는 동안 9분마다 다시 받는다.
const route = useRoute(), auth = useAuthStore()
const enrollId = Number(route.params.enroll), itemId = Number(route.params.item)
const review = ref(null), error = ref(''), pageNumber = ref(1)
const page = computed(() => review.value?.pages.find(item => item.number === pageNumber.value) || null)
const timed = computed(() => ['AUDIO', 'VIDEO'].includes(page.value?.mediaKind))
const frameAttrs = computed(() => (page.value ? documentFrame(page.value.source, window.location.origin) : {}))
let alive = true, renew = null
async function open() {
  clearTimeout(renew)
  try {
    const value = await auth.client.request(`/api/user/learning/enrollments/${enrollId}/review/${itemId}`, { method: 'POST' })
    if (!alive) return
    review.value = value; error.value = ''
    if (!value.pages.some(item => item.number === pageNumber.value)) pageNumber.value = 1
    renew = setTimeout(open, 9 * 60 * 1000)
  } catch (cause) { if (alive) { review.value = null; error.value = messageOf(cause) } }
}
onMounted(open)
onBeforeUnmount(() => { alive = false; clearTimeout(renew) })
useHead(() => ({ title: `${review.value?.title || '복습 학습'} · TECHEDU` }))
</script>
<template>
  <section class="portal-section portal-info"><div class="portal-container review-page">
    <nav class="portal-breadcrumb" aria-label="현재 위치"><NuxtLink to="/">홈</NuxtLink><span aria-hidden="true">›</span><NuxtLink to="/my">나의강의실</NuxtLink><span aria-hidden="true">›</span><NuxtLink to="/my/ended">학습종료 과정</NuxtLink><span aria-hidden="true">›</span><span>복습 학습</span></nav>
    <h1>{{ review?.title || '복습 학습' }}</h1>
    <p class="review-notice" role="note">복습 학습은 진도율·수료에 반영되지 않습니다.<template v-if="review?.reviewUntil"> 복습기간 : <strong>{{ review.reviewUntil }}</strong>까지</template></p>
    <div v-if="error" class="review-alert"><p role="alert">{{ error }}</p><div class="review-actions"><button type="button" class="portal-btn" @click="open">다시 시도</button><NuxtLink class="portal-btn secondary" to="/my/ended">학습종료 과정으로</NuxtLink></div></div>
    <p v-else-if="!review" role="status">복습할 콘텐츠를 준비하고 있습니다…</p>
    <section v-else class="review-player" aria-label="복습 재생">
      <nav v-if="review.pages.length > 1" class="review-pages" aria-label="복습 페이지"><button v-for="item in review.pages" :key="item.number" type="button" :aria-current="item.number === pageNumber ? 'page' : undefined" @click="pageNumber = item.number">{{ item.number }}페이지 · {{ item.name }}</button></nav>
      <component :is="page.mediaKind === 'AUDIO' ? 'audio' : 'video'" v-if="page && timed" :key="`${pageNumber}-${page.source}`" :src="page.source" controls preload="metadata" />
      <img v-else-if="page?.mediaKind === 'IMAGE'" :key="`img-${pageNumber}`" class="review-image" :src="page.source" :alt="page.name" referrerpolicy="no-referrer">
      <iframe v-else-if="page" :key="`doc-${pageNumber}`" v-bind="frameAttrs" class="review-document" :src="page.source" :title="page.name" referrerpolicy="no-referrer" />
      <div class="review-actions"><NuxtLink class="portal-btn secondary" to="/my/ended">학습종료 과정으로</NuxtLink></div>
    </section>
  </div></section>
</template>
<style scoped>
.review-page{max-width:1100px;overflow-wrap:anywhere}
.review-page h1{margin:14px 0;font-size:30px}
.review-notice{margin:0 0 18px;padding:12px 16px;border-left:4px solid #f6b618;background:#fffbea;color:#5b4a12}
.review-alert,.review-player{padding:24px;margin:16px 0;border:1px solid #dce5ef;border-radius:8px;background:#fff}
.review-pages{display:flex;flex-wrap:wrap;gap:8px;margin:0 0 16px}
.review-pages button{padding:8px 12px;border:1px solid #c7d4e5;border-radius:6px;background:#fff;text-align:left;cursor:pointer}
.review-pages button[aria-current=page]{border-color:#2665ae;background:#eaf2f8;color:#2665ae;font-weight:700}
.review-player video,.review-player audio{display:block;width:100%;max-height:620px;background:#000}
.review-player audio{background:none}
.review-image{display:block;max-width:100%;margin:0 auto}
.review-document{display:block;width:100%;height:72vh;border:1px solid #dce5ef}
.review-actions{display:flex;flex-wrap:wrap;gap:8px;margin-top:16px}
</style>
